import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  HeroImages,
} from "../../constants";

import {
  useStateContext,
} from "../../context/ContextProvider";

import {
  useTranslation,
} from "react-i18next";

import {
  FiArrowLeft,
  FiArrowRight,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";

import {
  FaUtensils,
} from "react-icons/fa";

import {
  AnimatePresence,
  motion,
} from "framer-motion";

import {
  Link,
} from "react-router-dom";


function Slider() {
  /* =====================================
      STATES
  ====================================== */

  const [
    currentIndex,
    setCurrentIndex,
  ] = useState(0);


  const [
    isHovering,
    setIsHovering,
  ] = useState(false);


  /* =====================================
      CONTEXT
  ====================================== */

  const {
    currentDir,
  } = useStateContext();


  const {
    t,
  } = useTranslation();


  const isRTL =
    currentDir === "rtl";


  /* =====================================
      CURRENT SLIDE
  ====================================== */

  const currentSlide =
    HeroImages[currentIndex];


  /* =====================================
      NAVIGATION
  ====================================== */

  const goToSlide =
    useCallback(
      (index) => {

        let nextIndex =
          index;


        if (
          nextIndex >=
          HeroImages.length
        ) {
          nextIndex = 0;
        }


        if (
          nextIndex < 0
        ) {
          nextIndex =
            HeroImages.length -
            1;
        }


        setCurrentIndex(
          nextIndex
        );

      },
      []
    );


  const nextSlide =
    useCallback(
      () => {

        goToSlide(
          currentIndex + 1
        );

      },
      [
        currentIndex,
        goToSlide,
      ]
    );


  const prevSlide =
    useCallback(
      () => {

        goToSlide(
          currentIndex - 1
        );

      },
      [
        currentIndex,
        goToSlide,
      ]
    );


  /* =====================================
      AUTO PLAY
  ====================================== */

  useEffect(() => {

    if (
      isHovering
    ) {
      return;
    }


    const interval =
      setInterval(
        () => {

          setCurrentIndex(
            (prev) =>
              (
                prev + 1
              ) %
              HeroImages.length
          );

        },
        6000
      );


    return () =>
      clearInterval(
        interval
      );

  }, [
    isHovering,
  ]);


  /* =====================================
      DIRECTION ARROW
  ====================================== */

  const DirectionArrow =
    isRTL
      ? FiArrowLeft
      : FiArrowRight;


  return (
    <section
      className="
        relative

        w-full

        h-[100svh]

        min-h-[680px]
        md:min-h-[720px]

        overflow-hidden

        bg-color-2
      "

      onMouseEnter={() =>
        setIsHovering(true)
      }

      onMouseLeave={() =>
        setIsHovering(false)
      }
    >

      {/* =====================================
          BACKGROUND IMAGE
      ====================================== */}

      <AnimatePresence
        mode="wait"
      >

        <motion.div
          key={
            currentIndex
          }

          initial={{
            opacity: 0,
            scale: 1.04,
          }}

          animate={{
            opacity: 1,
            scale: 1,
          }}

          exit={{
            opacity: 0,
            scale: 1.02,
          }}

          transition={{
            duration: 1.15,
            ease: [
              0.22,
              1,
              0.36,
              1,
            ],
          }}

          className="
            absolute
            inset-0
          "
        >

    <img
  src={currentSlide.img}
  alt=""
  className={`
    w-full
    h-full

    object-cover

    transition-transform
    duration-700

    ${
      isRTL
        ? "scale-x-100"
        : "-scale-x-100"
    }

    object-[67%_center]
    md:object-center
  `}
/>
        </motion.div>

      </AnimatePresence>


      {/* =====================================
          VERY LIGHT GLOBAL OVERLAY

          الصور الجديدة مصممة مسبقاً
          لذلك نستخدم تعتيم خفيف جداً.
      ====================================== */}

      <div
        className="
          absolute
          inset-0

          bg-black/[0.015]

          pointer-events-none
        "
      />


      {/* =====================================
          DESKTOP SOFT BOTTOM SHADE

          فقط لتحسين أزرار السلايدر
          في الجزء السفلي.
      ====================================== */}

      <div
        className="
          absolute
          inset-0

          bg-gradient-to-t

          from-black/20
          via-transparent
          to-transparent

          pointer-events-none
        "
      />


      {/* =====================================
          SUBTLE GOLD ATMOSPHERE
      ====================================== */}

      <div
        className={`
          absolute

          top-[22%]

          w-[250px]
          h-[250px]

          md:w-[420px]
          md:h-[420px]

          rounded-full

          bg-color-1/[0.08]

          blur-[130px]

          pointer-events-none

          ${
            isRTL
              ? "-right-48"
              : "-left-48"
          }
        `}
      />


      {/* =====================================
          MAIN CONTENT
      ====================================== */}

      <div
        className="
          relative
          z-20

          container

          h-full

          flex

          items-start
          md:items-center

          pt-[34vh]
          sm:pt-[31vh]
          md:pt-32

          pb-28
          md:pb-32
        "
      >

        {/* =================================
            CONTENT COLUMN
        ================================= */}

        <div
          className={`
            relative

            w-full

            max-w-[720px]

            ${
              isRTL
                ? "ml-auto text-right"
                : "mr-auto text-left"
            }
          `}
        >

          {/* =================================
              MOBILE READABILITY GLOW

              ليست بطاقة ظاهرة،
              مجرد إضاءة كريمية خلف النص.
          ================================= */}

          <div
            className="
              absolute

              md:hidden

              -inset-x-4
              -top-7
              -bottom-7

              rounded-[2.5rem]

              bg-[#EFDEC9]/55

              blur-[28px]

              pointer-events-none
            "
          />


          {/* Second subtle mobile layer */}

          <div
            className="
              absolute

              md:hidden

              -inset-x-2
              -top-3
              -bottom-3

              rounded-[2rem]

              bg-gradient-to-b

              from-[#F7EBDD]/25
              via-[#EFDEC9]/38
              to-[#EFDEC9]/10

              pointer-events-none
            "
          />


          <div
            className="
              relative
              z-10
            "
          >

            <AnimatePresence
              mode="wait"
            >

              <motion.div
                key={
                  currentIndex
                }

                initial={{
                  opacity: 0,
                  y: 25,
                }}

                animate={{
                  opacity: 1,
                  y: 0,
                }}

                exit={{
                  opacity: 0,
                  y: -15,
                }}

                transition={{
                  duration: 0.65,
                  ease: "easeOut",
                }}
              >

                {/* =========================
                    SMALL BRAND LABEL
                ========================== */}

                <motion.div
                  initial={{
                    opacity: 0,

                    x:
                      isRTL
                        ? 20
                        : -20,
                  }}

                  animate={{
                    opacity: 1,
                    x: 0,
                  }}

                  transition={{
                    duration: 0.6,
                    delay: 0.08,
                  }}

                  className="
                    flex

                    items-center

                    gap-3

                    mb-3
                    md:mb-7
                  "
                >

                  {/* Line */}

                  <div
                    className="
                      w-10
                      sm:w-12
                      md:w-16

                      h-[2px]

                      rounded-full

                      bg-gradient-to-l

                      from-[#442514]
                      to-[#A5773A]
                    "
                  />


                  {/* Label */}

                  <div
                    className="
                      flex

                      items-center

                      gap-2

                      text-[#A5773A]
                    "
                  >

                    <FaUtensils
                      className="
                        text-[10px]
                        md:text-sm
                      "
                    />


                    <span
                      className="
                        font-kufi
                        font-medium

                        text-[10px]
                        sm:text-xs
                        md:text-sm

                        whitespace-nowrap
                      "
                    >
                      {t(
                        "authentic yemeni cuisine"
                      )}
                    </span>

                  </div>

                </motion.div>


                {/* =========================
                    MAIN TITLE
                ========================== */}

                <motion.h1
                  initial={{
                    opacity: 0,
                    y: 20,
                  }}

                  animate={{
                    opacity: 1,
                    y: 0,
                  }}

                  transition={{
                    duration: 0.7,
                    delay: 0.12,
                  }}

                  className="
                    relative

                    max-w-[700px]

                    overflow-visible

                    font-kufi
                    font-bold

                    text-[2.15rem]
                    leading-[1.65]

                    sm:text-[2.8rem]
                    sm:leading-[1.55]

                    md:text-[3.5rem]
                    md:leading-[1.5]

                    lg:text-[3.8rem]

                    xl:text-[4.1rem]
                    xl:leading-[1.48]

                    mb-1
                    md:mb-2

                    pb-[0.15em]
                  "
                >

                  {/*
                    نضع Gradient داخل Span منفصل
                    حتى لا يقص Chrome أطراف
                    الحروف العربية.
                  */}

                  <span
                    className="
                      inline-block

                      overflow-visible

                      pb-[0.12em]

                      text-transparent

                      bg-clip-text
                    "

                    style={{
                      backgroundImage:
                        "linear-gradient(105deg, #442514 4%, #6B3C20 32%, #A5773A 67%, #6E421F 83%, #442514 100%)",

                      WebkitBackgroundClip:
                        "text",

                      WebkitTextFillColor:
                        "transparent",

                      filter:
                        "drop-shadow(0 4px 12px rgba(68,37,20,0.07))",
                    }}
                  >
                    {currentDir ===
                    "ltr"
                      ? currentSlide.titleEn
                      : currentSlide.titleAr}
                  </span>

                </motion.h1>


                {/* =========================
                    TITLE ACCENT
                ========================== */}

                <motion.div
                  initial={{
                    width: 0,
                  }}

                  animate={{
                    width: 72,
                  }}

                  transition={{
                    duration: 0.8,
                    delay: 0.24,
                  }}

                  className="
                    h-[3px]

                    rounded-full

                    bg-gradient-to-l

                    from-[#442514]
                    via-[#A5773A]
                    to-[#D1A15D]

                    mb-4
                    md:mb-7
                  "
                />


                {/* =========================
                    DESCRIPTION
                ========================== */}

                <motion.p
                  initial={{
                    opacity: 0,
                    y: 16,
                  }}

                  animate={{
                    opacity: 1,
                    y: 0,
                  }}

                  transition={{
                    duration: 0.7,
                    delay: 0.2,
                  }}

                  className="
                    whitespace-pre-line

                    max-w-[590px]

                    font-kufi
                    font-medium

                    text-[#442514]

                    text-[11px]
                    leading-[1.9rem]

                    sm:text-[12px]
                    sm:leading-8

                    md:text-[15px]
                    md:leading-9

                    lg:text-base

                    mb-5
                    md:mb-8
                  "

                  style={{
                    textShadow:
                      "0 1px 10px rgba(247,235,221,0.85)",
                  }}
                >
                  {currentDir ===
                  "ltr"
                    ? currentSlide.subTitleEn
                    : currentSlide.subTitleAr}
                </motion.p>


                {/* =========================
                    CTA
                ========================== */}

                <motion.div
                  initial={{
                    opacity: 0,
                    y: 18,
                  }}

                  animate={{
                    opacity: 1,
                    y: 0,
                  }}

                  transition={{
                    duration: 0.7,
                    delay: 0.28,
                  }}
                >

                  <Link
                    to="/MealsCategories"

                    className="
                      group

                      inline-flex

                      min-h-[52px]
                      md:min-h-[58px]

                      items-center
                      justify-center

                      gap-3

                      px-6
                      sm:px-8
                      md:px-10

                      rounded-full

                      bg-[#442514]

                      border
                      border-[#442514]

                      text-white

                      font-kufi
                      font-semibold

                      text-[11px]
                      sm:text-sm
                      md:text-base

                      shadow-[0_15px_38px_rgba(68,37,20,0.20)]

                      transition-all
                      duration-300

                      hover:bg-[#A5773A]
                      hover:border-[#A5773A]

                      hover:-translate-y-1

                      hover:shadow-[0_18px_45px_rgba(165,119,58,0.25)]
                    "
                  >

                    <span>
                      {t(
                        "explore menu"
                      )}
                    </span>


                    <DirectionArrow
                      className="
                        text-base
                        md:text-lg

                        transition-transform
                        duration-300

                        group-hover:-translate-x-1

                        rtl:group-hover:translate-x-1
                      "
                    />

                  </Link>

                </motion.div>

              </motion.div>

            </AnimatePresence>

          </div>

        </div>

      </div>


      {/* =====================================
          BOTTOM NAVIGATION
      ====================================== */}

      <div
        className="
          absolute

          z-30

          bottom-0
          start-0

          w-full

          pointer-events-none
        "
      >

        <div
          className="
            container

            pb-5
            sm:pb-6
            md:pb-9
          "
        >

          <div
            className="
              flex

              items-center
              justify-between

              gap-4
              md:gap-6

              pointer-events-auto
            "
          >

            {/* =================================
                SLIDER INDICATORS
            ================================= */}

            <div
              className="
                flex

                flex-1

                max-w-[210px]
                sm:max-w-[260px]
                md:max-w-[360px]

                items-center

                gap-2
                md:gap-3
              "
            >

              {HeroImages.map(
                (
                  _,
                  index
                ) => {

                  const active =
                    currentIndex ===
                    index;


                  return (
                    <button
                      type="button"

                      key={
                        index
                      }

                      onClick={() =>
                        goToSlide(
                          index
                        )
                      }

                      aria-label={`${t(
                        "go to slide"
                      )} ${
                        index + 1
                      }`}

                      className={`
                        relative

                        h-[3px]

                        overflow-hidden

                        rounded-full

                        transition-all
                        duration-500

                        ${
                          active
                            ? `
                              w-12
                              sm:w-16
                              md:w-24

                              bg-[#442514]/20
                            `
                            : `
                              w-6
                              sm:w-7
                              md:w-10

                              bg-[#442514]/20

                              hover:bg-[#442514]/40
                            `
                        }
                      `}
                    >

                      {active && (

                        <motion.span
                          key={`${currentIndex}-${index}`}

                          initial={{
                            width:
                              "0%",
                          }}

                          animate={{
                            width:
                              "100%",
                          }}

                          transition={{
                            duration: 6,
                            ease: "linear",
                          }}

                          className="
                            absolute

                            inset-y-0
                            start-0

                            rounded-full

                            bg-[#A5773A]
                          "
                        />

                      )}

                    </button>
                  );

                }
              )}

            </div>


            {/* =================================
                PREVIOUS / NEXT
            ================================= */}

            <div
              className="
                flex

                items-center

                gap-2
              "
            >

              {/* Previous */}

              <motion.button
                type="button"

                whileHover={{
                  scale: 1.05,
                }}

                whileTap={{
                  scale: 0.95,
                }}

                onClick={
                  prevSlide
                }

                aria-label={t(
                  "previous slide"
                )}

                className="
                  flex

                  w-10
                  h-10

                  md:w-12
                  md:h-12

                  items-center
                  justify-center

                  rounded-full

                  border
                  border-[#442514]/20

                  bg-[#EFDEC9]/20

                  backdrop-blur-md

                  text-[#442514]

                  shadow-sm

                  transition-all
                  duration-300

                  hover:border-[#A5773A]

                  hover:bg-[#A5773A]
                  hover:text-white
                "
              >

                {isRTL ? (

                  <FiChevronRight
                    className="
                      text-lg
                      md:text-xl
                    "
                  />

                ) : (

                  <FiChevronLeft
                    className="
                      text-lg
                      md:text-xl
                    "
                  />

                )}

              </motion.button>


              {/* Next */}

              <motion.button
                type="button"

                whileHover={{
                  scale: 1.05,
                }}

                whileTap={{
                  scale: 0.95,
                }}

                onClick={
                  nextSlide
                }

                aria-label={t(
                  "next slide"
                )}

                className="
                  flex

                  w-10
                  h-10

                  md:w-12
                  md:h-12

                  items-center
                  justify-center

                  rounded-full

                  border
                  border-[#442514]/20

                  bg-[#EFDEC9]/20

                  backdrop-blur-md

                  text-[#442514]

                  shadow-sm

                  transition-all
                  duration-300

                  hover:border-[#A5773A]

                  hover:bg-[#A5773A]
                  hover:text-white
                "
              >

                {isRTL ? (

                  <FiChevronLeft
                    className="
                      text-lg
                      md:text-xl
                    "
                  />

                ) : (

                  <FiChevronRight
                    className="
                      text-lg
                      md:text-xl
                    "
                  />

                )}

              </motion.button>

            </div>

          </div>

        </div>

      </div>


      {/* =====================================
          BOTTOM PROGRESS LINE
      ====================================== */}

      <div
        className="
          absolute

          z-30

          bottom-0
          start-0

          w-full
          h-[2px]

          bg-[#442514]/10
        "
      >

        <motion.div
          animate={{
            width: `${
              (
                (
                  currentIndex +
                  1
                ) /
                HeroImages.length
              ) *
              100
            }%`,
          }}

          transition={{
            duration: 0.7,
          }}

          className="
            h-full

            bg-[#A5773A]
          "
        />

      </div>

    </section>
  );
}


export default Slider;